import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { MembershipRole, type AdminMembership } from "@/shared/organizations/membership-types";
import { summarizeMemberships } from "./membership-summary";

interface MembershipsSummaryProps {
  readonly memberships: readonly AdminMembership[];
}

const ROLES = [MembershipRole.OWNER, MembershipRole.ADMIN, MembershipRole.STAFF] as const;

/** How many organizations the user belongs to, active vs inactive, and the roles they hold. */
export function MembershipsSummary({ memberships }: MembershipsSummaryProps) {
  const { t } = useTranslation();
  const summary = useMemo(() => summarizeMemberships(memberships), [memberships]);
  return (
    <div
      data-testid="memberships-summary"
      className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground"
    >
      <span>{t("users.memberships.summary.total", { count: summary.total })}</span>
      <span>{t("users.memberships.summary.active", { count: summary.active })}</span>
      <span>{t("users.memberships.summary.inactive", { count: summary.inactive })}</span>
      <ul className="flex flex-wrap gap-x-3" aria-label={t("users.memberships.summary.byRole")}>
        {ROLES.map((role) => (
          <li key={role} data-testid={`memberships-summary-role-${role}`}>
            {t(`users.memberships.role.${role}`)}: {summary.byRole[role]}
          </li>
        ))}
      </ul>
    </div>
  );
}
